import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { InboxDto } from '@app/core/model';
import { InboundFaxApiService, LookupService, NotificationService } from '@app/core/service';
import { FormDialogComponent } from '@app/shared/components';
import { FormDialogConfig, InboundFaxesTableModel } from '@app/shared/models';
import { DialogConfig } from '@app/shared/utility';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { of } from 'rxjs';
import { InboundFaxesService } from './inbound-faxes.service';

@Injectable()
export class FaxStatusService {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly inboundFaxApiService = inject(InboundFaxApiService);
  private readonly inboundFaxesService = inject(InboundFaxesService);
  private readonly lookupService = inject(LookupService);
  private readonly notificationService = inject(NotificationService);

  public async handleClick(target: InboundFaxesTableModel, inbox: InboxDto): Promise<void> {
    if (!target.statusId) {
      await this.openAddDialog(target, inbox);
    } else {
      this.openRemoveDialog(target, inbox);
    }
  }

  private async openAddDialog(target: InboundFaxesTableModel, inbox: InboxDto): Promise<void> {
    const statuses = (await this.lookupService.getStatusReferences()).map(
      (x): SelectItem => ({ label: x.description, value: x.referenceByEntityId })
    );
    const formDialogConfigs: FormDialogConfig[] = [
      {
        label: 'Fax Status',
        type: 'dropdown',
        formControlName: 'status',
        options: statuses,
        validators: [Validators.required],
      },
    ];

    this.dialogService
      .open(
        FormDialogComponent,
        DialogConfig('Add Fax Status', {
          submitLabel: 'Add',
          formDialogConfigs: of(formDialogConfigs),
        })
      )
      .onClose.subscribe((res: object) => {
        if (res) {
          this.updateStatus(target.inboundFaxId, res['status'], inbox);
        }
      });
  }

  private openRemoveDialog(target: InboundFaxesTableModel, inbox: InboxDto): void {
    this.confirmationService.confirm({
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      header: 'Remove Fax Status',
      message: 'Are you sure that you want to remove the fax status?',
      accept: () => this.updateStatus(target.inboundFaxId, null, inbox),
      reject: () => {},
    });
  }

  private updateStatus(inboundFaxId: string, updateId: string, inbox: InboxDto): void {
    this.inboundFaxesService.isLoadingTableData$.next(true);

    this.inboundFaxApiService
      .updateStatus(inboundFaxId, { updateId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            'Successfully Updated Status',
            'Status has been updated to the fax record.'
          );
          this.inboundFaxesService.reload(inbox);
        },
        error: () => {
          this.notificationService.showError(
            'Unable to Update Fax Status',
            'There was an issue updating the fax status. Please try again later.'
          );
          this.inboundFaxesService.isLoadingTableData$.next(false);
        },
      });
  }
}
